import React, { useState, useEffect, useRef, useCallback } from "react";
import { cls } from "cls";
import { baseFieldStyle } from "../../Utilities/customClassTypes";
import useAddressSuggestions from "../../Utilities/useAddressSuggestions";

interface FormAddressProps {
  label: string;
  inputName: string;
  errorMessage?: string;
  setFormValue: (key: string, value: string | number | boolean) => void;
}

const FormAddress = ({
  inputName,
  errorMessage,
  setFormValue,
}: FormAddressProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const { suggestions, updateSuggestions, clearSuggestions } =
    useAddressSuggestions();

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const handleDropdown = useCallback(
    (e: MouseEvent) => {
      if (!(e.target instanceof Element)) return;
      if (
        dropdownRef.current &&
        isVisible &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsVisible(false);
      }

      if (
        inputRef.current &&
        !isVisible &&
        inputRef.current.contains(e.target) &&
        suggestions.length > 0
      ) {
        setIsVisible(true);
      }
    },
    [isVisible, suggestions.length],
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleDropdown);
  }, [handleDropdown]);

  useEffect(
    () =>
      setIsVisible(
        inputRef.current !== null &&
          inputRef.current.value.length > 0 &&
          suggestions.length > 0,
      ),
    [suggestions],
  );

  const handleSelectSuggestion = (suggestion: string) => {
    if (inputRef.current) {
      inputRef.current.value = suggestion;
    }

    setFormValue(inputName, suggestion);
    clearSuggestions();
  };

  return (
    <>
      <input
        id="formAddress"
        type="text"
        name={inputName}
        ref={inputRef}
        className={baseFieldStyle(!!errorMessage)}
        onChange={(event) => {
          setFormValue(inputName, event.target.value);

          if (event.target.value) {
            updateSuggestions(event.target.value);
          } else {
            clearSuggestions();
          }
        }}
      />
      {isVisible && (
        <ul
          ref={dropdownRef}
          className={`${cls(
            "bg-white",
            "border-gray-200",
            "rounded-sm",
            "border",
            "border-solid",
            "ml-6",
            "md:ml-20",
            "overflow-y-auto",
            "p-0",
            "absolute",
            "inset-x-0",
            "shadow-lg",
            "z-10",
          )}
            mr-[22px]
            md:mr-[78px]
            mt-[68px]
          `}
          style={{ maxHeight: "180px" }}
        >
          {suggestions.map((suggestion: string) => (
            <li
              className={`suggestion ${cls(
                "hover:bg-green-500",
                "focus:bg-green-500",
                "box-border",
                "cursor-pointer",
                "font-bold",
                "text-sm",
                "list-none",
                "py-2",
                "px-4",
                "text-left",
                "hover:text-white",
                "focus:text-white",
              )}`}
              onClick={() => handleSelectSuggestion(suggestion)}
              key={suggestion}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default FormAddress;
